/* eslint jsx-a11y/no-static-element-interactions: 0 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { isEqual, debounce } from 'lodash';
import { AutoSizer } from 'react-virtualized';
import { Classes, Hotkeys, Hotkey, HotkeysTarget } from '@blueprintjs/core';

import Flex from 'core/components/Flex';
import Icon from 'core/components/Icon';
import Tag from 'core/components/Tag';
import Text from 'core/components/Text';
import InputGroup from 'core/form/components/InputGroup';
import { DOWN, ENTER, ESC, SPACE, TAB, UP } from 'core/util/keyCodes';
import makeCancelable from 'core/util/cancelablePromise';
import { filterForAriaProps, guessTextFromLabel } from '../../util/ariaUtils';

import SelectPopover from './SelectPopover';
import MultiSelectInput from './MultiSelectInput';
import SelectButton from './SelectButton';

export const defaultValueRenderer = (option, placeholder, values, options, showCount) => {
  if (!option || showCount) {
    return (
      <Flex alignItems="center" gap="4px" justifyContent="flex-start">
        <span className={!values?.length ? Classes.TEXT_MUTED : ''}>{placeholder || 'Select a value...'}</span>
        {showCount && values?.length > 0 && (
          <Tag ellipsis={false} intent="primary" round>
            {values.length}
          </Tag>
        )}
      </Flex>
    );
  }

  const iconName = option ? option.iconCls || option.iconName || option.icon : false;
  return (
    <Flex justifyContent="flex-start" alignItems="center" className={option.className}>
      {iconName && <Icon icon={iconName} style={{ marginRight: 6 }} />}
      {option && <Text ellipsis>{option.label}</Text>}
    </Flex>
  );
};

const TARGET_COMPONENT_OPTIONS = {
  MultiSelectInput: 'MultiSelectInput',
  SelectButton: 'SelectButton',
  InputGroup: 'InputGroup'
};

@observer
@HotkeysTarget
class Select extends Component {
  static defaultProps = {
    exactMatch: true,
    onQuery: null,
    disabled: false,
    loading: false,
    multi: false,
    clearable: false,
    clearableLabel: 'None',
    valueRenderer: defaultValueRenderer,
    optionLimit: -1,
    minFilterChars: 0,
    menuWidth: 230,
    fill: false,
    showCount: false,
    showFilter: false,
    keepOpen: false,
    preventEnterSelection: false,
    labelId: null,
    hideSelected: false,
    blockDefaultHotkeys: false,
    componentRef: null
  };

  state = {
    targetComponentName: TARGET_COMPONENT_OPTIONS.InputGroup,
    isOpen: false,
    options: null,
    optionsFilter: '',
    selectedIndex: -1
  };

  setTargetComponent() {
    const { exactMatch, tagInput, values, renderAsButton, multi } = this.props;
    const showButton = renderAsButton || tagInput;
    const tagInputWithValues = tagInput && Array.isArray(values) && values.length > 0;

    const { targetComponentName } = this.state;

    // this came from component selection logic in renderSelectPopoverBody: MultiSelectInput, InputGroup or SelectButton
    if (multi && (!showButton || tagInputWithValues)) {
      if (targetComponentName !== TARGET_COMPONENT_OPTIONS.MultiSelectInput) {
        this.setState({ targetComponentName: TARGET_COMPONENT_OPTIONS.MultiSelectInput });
      }
    } else if (!exactMatch && !showButton) {
      if (targetComponentName !== TARGET_COMPONENT_OPTIONS.InputGroup) {
        this.setState({ targetComponentName: TARGET_COMPONENT_OPTIONS.InputGroup });
      }
    } else if (targetComponentName !== TARGET_COMPONENT_OPTIONS.SelectButton) {
      this.setState({ targetComponentName: TARGET_COMPONENT_OPTIONS.SelectButton });
    }
  }

  componentDidMount() {
    const { values, exactMatch, componentRef } = this.props;
    this.targetEl = React.createRef();

    if (exactMatch && values && (!Array.isArray(values) || values.length > 0)) {
      this.filterOptions(values, { debounce: false });
    }

    this.closeOptions = this.closeOptions.bind(this);
    this.setTargetComponent();

    if (componentRef) {
      componentRef.current = this;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen, values, exactMatch, onOpen } = this.props;
    const { isOpen: stateIsOpen } = this.state;

    if (onOpen && !prevState.isOpen && stateIsOpen) {
      onOpen();
    }

    if (!prevProps.isOpen && isOpen) {
      this.showOptions();
    }

    if (exactMatch && values && values !== prevProps.values && (!Array.isArray(values) || values.length > 0)) {
      this.filterOptions(values, { debounce: false });
    }
    this.setTargetComponent();
  }

  componentWillUnmount() {
    const { componentRef } = this.props;
    if (componentRef) {
      componentRef.current = null;
    }
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleScrollRef = (refs) => {
    this.scrollEl = refs;
  };

  handleSelectItems = (values, options) => {
    const { onChange, values: currentValues, keepOpen } = this.props;

    if (options) {
      const { options: currentOptions } = this.state;

      if (currentOptions) {
        this.setState({
          options: currentOptions.concat(
            options.filter((option) => !currentOptions.find((o) => o.value === option.value))
          )
        });
      } else {
        this.setState({ options });
      }
    }

    const filteredOptions = this.getFilteredOptions();
    /* eslint-disable eqeqeq */
    const selectedIndex = filteredOptions.findIndex((opt) => opt.value == values[0] || isEqual(opt.value, values[0]));

    this.setState({ selectedIndex });

    onChange(currentValues.concat(values.filter((value) => !currentValues.find((v) => v === value))));

    if (!keepOpen) {
      this.closeOptions();
    }
  };

  handleSelectItem = (value, option, focusOnKeepOpen = false) => {
    const { onChange, clearable, multi, values, keepOpen, showClearButton, toggle } = this.props;
    let isDivider = false;
    if (Array.isArray(value)) {
      onChange(
        [].concat(
          values,
          value.filter((v) => !values.includes(v))
        )
      );
    } else {
      if (option) {
        const { options: currentOptions } = this.state;
        if (multi && currentOptions && !currentOptions.some((opt) => opt.value === value)) {
          currentOptions.push(option);
        } else {
          this.state.options = [option];
        }
      }

      /* eslint-disable eqeqeq */
      const isValueEqual = (a, b) => a == b || isEqual(a, b);

      const options = this.getFilteredOptions();
      const selectedIndex = options.findIndex((opt) => isValueEqual(opt.value, value));
      isDivider = options[selectedIndex]?.separator === true;

      // Concern: it is finding the index bc both are undefined so this could be a little ambiguous
      if (!isDivider) {
        this.setState({ selectedIndex });

        if (multi) {
          const alreadySelected = values.some((val) => isValueEqual(val, value));

          if ((clearable || showClearButton) && value === '') {
            onChange([]);
          } else if (!alreadySelected) {
            onChange(values.concat(value));
          } else if (toggle) {
            onChange(values.filter((val) => !isValueEqual(val, value)));
          }
        } else {
          onChange(value);
        }
      }
    }

    // ME: might be easier to let this run?
    if (!keepOpen && !isDivider) {
      this.closeOptions();
    } else if (focusOnKeepOpen && multi) {
      // At least in multi, we seem to lose focus to the overlay for unknown reasons after select and this is the most consistent way to get it back.
      // keepOpen is nearly always used with multi so we have the extra condition but we might want to test this without multi
      // Alt idea: focus the filter, if its available
      this.updateScrollPosition();
    }
  };

  // returns a closure, of course
  handleUnselectItem = (value) => {
    const { onChange, values } = this.props;

    return (e) => {
      e.stopPropagation();

      /* eslint-disable eqeqeq */
      const idx = values.findIndex((currValue) => currValue == value || isEqual(currValue, value));
      const newValues = values.slice();
      newValues.splice(idx, 1);
      onChange(newValues);
    };
  };

  setIndexManually = (index) => {
    const { selectedIndex } = this.state;
    if (selectedIndex !== index) {
      this.setState({ selectedIndex: index }, this.updateScrollPosition);
    }
  };

  handleKeyboardEvents = (e, keyOrigin) => {
    const { multi, values } = this.props;
    const { isOpen, selectedIndex } = this.state;
    const selectOptions = this.getFilteredOptions();

    let skipTargetFocus = false;
    const target = this.targetEl?.current;

    if (target) {
      if (typeof target.contains === 'function') {
        skipTargetFocus = target.contains(e.target);
      } else if (target.buttonRef && typeof target.buttonRef.contains === 'function') {
        skipTargetFocus = target.buttonRef.contains(e.target);
      }
    }

    const key = e.keyCode ? e.keyCode : e.which;
    const keyCodes = [UP, DOWN, ENTER, ESC, TAB];

    if (keyOrigin !== 'filterInput') {
      keyCodes.push(SPACE);
    }

    if (keyCodes.includes(key)) {
      e.stopPropagation();
      if (key === DOWN) {
        this.setState(
          {
            selectedIndex: selectedIndex < selectOptions.length - 1 ? selectedIndex + 1 : 0
          },
          this.updateScrollPosition
        );
      }
      if (key === UP) {
        this.setState(
          {
            selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : selectOptions.length - 1
          },
          this.updateScrollPosition
        );
      }

      if (key === ENTER || (keyOrigin !== 'filterInput' && key === SPACE)) {
        if (selectedIndex !== -1) {
          const option = selectOptions[selectedIndex];
          if (option) {
            if (multi) {
              /* eslint-disable eqeqeq */
              const idx = values.findIndex(
                (currValue) => currValue == option.value || isEqual(currValue, option.value)
              );
              if (idx === -1) {
                this.handleSelectItem(option.value, option);
              } else {
                this.handleUnselectItem(option.value);
              }
            } else {
              this.handleSelectItem(option.value, option);
            }
          } else {
            this.setState({ isOpen: false, selectedIndex: -1 });
          }
        } else {
          this.closeOptions({ skipTargetFocus });
        }
      }

      if (key === ESC || key === TAB) {
        this.setState({ selectedIndex: -1 });
        this.closeOptions({ skipTargetFocus });
      }
    } else if (!isOpen) {
      this.setState({ isOpen: true });
    }
  };

  handleEsc = (e) => {
    const { isOpen } = this.state;
    const key = e.keyCode ? e.keyCode : e.which;

    if (key === ESC) {
      if (isOpen) {
        e.stopPropagation();
      }
      this.setState({ isOpen: false, selectedIndex: -1 });
    }
  };

  updateScrollPosition = () => {
    const { selectedIndex } = this.state;
    if (this.scrollEl) {
      const itemHeight = this.scrollEl.firstChild.getBoundingClientRect().height;
      const selectedIndexY = (selectedIndex + 1) * itemHeight;

      if (selectedIndexY > this.scrollEl.scrollTop + this.scrollEl.offsetHeight) {
        this.scrollEl.scrollTop = selectedIndexY - this.scrollEl.offsetHeight + itemHeight;
      } else if (selectedIndexY - itemHeight < this.scrollEl.scrollTop) {
        this.scrollEl.scrollTop = selectedIndexY - itemHeight;
      }
    }
    if (this.focusedOption) {
      const y = this.focusedOption.scrollTop;
      const x = this.focusedOption.scrollLeft;

      setTimeout(() => {
        this.focusedOption?.focus({ preventScroll: false });
      }, 0);
      this.focusedOption?.scrollTo(x, y);
    }
  };

  handleRef = (refs) => {
    this.wrapperEl = refs;
  };

  handlePopoverRef = (refs, isItem = false) => {
    if (isItem) {
      this.focusedOption = refs;
    } else {
      this.popoverEl = refs;
    }
  };

  handleDocumentClick = (e) => {
    if (this.wrapperEl && !this.wrapperEl.contains(e.target) && this.popoverEl && !this.popoverEl.contains(e.target)) {
      this.closeOptions({ skipTargetFocus: true });
    }
  };

  handleShowOptions = () => {
    const { isOpen } = this.state;
    if (!isOpen) {
      this.showOptions();
    } else {
      this.closeOptions();
    }
  };

  handleTargetFocus = () => {
    const { isOpen } = this.state;

    if (!isOpen) {
      this.showOptions();
    }
  };

  handleAutoCompleteChange = (e) => {
    const { onChange } = this.props;
    const { value } = e.target;

    this.filterOptions(value);

    onChange(value);
  };

  showOptions() {
    const { showFilter, values } = this.props;

    if (!showFilter) {
      this.filterOptions(values);
    }
    this.setState({ isOpen: true });
    document.addEventListener('click', this.handleDocumentClick);
  }

  delayFocusTarget = debounce(() => this.focusTarget.call(this), 200);

  focusTarget = () => {
    if (this.targetEl?.current?.focus) {
      this.targetEl.current.focus({ preventScroll: true });
    } else if (this.targetEl?.current?.buttonRef) {
      this.targetEl.current.buttonRef.focus({ preventScroll: true });
    }
  };

  closeOptions({ skipTargetFocus = false } = {}) {
    const { onClose } = this.props;
    const { targetComponentName } = this.state;

    this.setState({ isOpen: false });

    document.removeEventListener('click', this.handleDocumentClick);

    // if we are NOT focused on the target, then we want to return focus to the target
    // BUT if you hit Tab, at that insant the target is NOT focused . . problems.  So we need to delay this
    if (!skipTargetFocus) {
      this.delayFocusTarget();
    }

    // bc this has autoOpen behavior so we would have an inf loop of sorts
    if (targetComponentName === TARGET_COMPONENT_OPTIONS.InputGroup) {
      this.focusedOption = null;
    }
    if (onClose) {
      onClose();
    }
  }

  filterOptions(optionsFilter, onQueryOpts = {}) {
    const { id, onQuery, values, exactMatch, multi, keepOpen, showFilter } = this.props;

    if (onQuery) {
      if (this.onQueryPromise) {
        this.onQueryPromise.cancel();
      }

      this.setState({ fetchingOptions: !keepOpen });
      this.onQueryPromise = makeCancelable(onQuery(optionsFilter, { ...onQueryOpts, id, ignoreFilter: multi }));

      if (exactMatch) {
        const { selectedIndex, options } = this.state;
        const value =
          selectedIndex >= 0 && options && options.length > selectedIndex ? options[selectedIndex].value : values;

        this.onQueryPromise.promise.then((newOptions) => {
          const newSelectedIndex = newOptions.findIndex((option) => option.value === value);
          this.setState({ fetchingOptions: false, options: newOptions, selectedIndex: newSelectedIndex });
        });
      } else {
        this.onQueryPromise.promise.then((options) => {
          this.setState({ fetchingOptions: false, options, selectedIndex: -1 });
        });
      }
    } else {
      // onQuery is allowed to be null in SelectPopover but there will be no filtering if they both showFilter and onQuery are false/null
      // So if you do not want showFilter, add a lookup!
      this.setState({ selectedIndex: -1 });
      if (!showFilter) {
        console.warn(
          `Select component with id "${id}" is using filterOptions without onQuery. This will not filter options unless showFilter is true.`
        );
      }
    }
  }

  getFilteredOptions() {
    const { exactMatch, minFilterChars, onQuery, options, optionLimit } = this.props;
    const { optionsFilter, options: dynamicOptions } = this.state;

    let filteredOptions = options;

    if (onQuery && dynamicOptions) {
      filteredOptions = dynamicOptions;
    } else if (!exactMatch && optionsFilter) {
      const filter = optionsFilter.toLowerCase();
      filteredOptions = options.filter(
        ({ label, value, filterLabel }) =>
          (filterLabel && filterLabel.toLowerCase().includes(filter)) ||
          (typeof label === 'string' && label.toLowerCase().includes(filter)) ||
          (typeof value === 'string' && value.toLowerCase().includes(filter)) ||
          (typeof value === 'number' && value.toString().startsWith(filter))
      );
    }

    if (minFilterChars > 0 && optionsFilter.length < minFilterChars && !exactMatch) {
      return [];
    }

    const allFilteredOptions = filteredOptions;

    if (optionLimit > 0 && !exactMatch) {
      return allFilteredOptions.slice(0, optionLimit);
    }

    return allFilteredOptions;
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  renderHotkeys() {
    const { blockDefaultHotkeys } = this.props;
    const { isOpen } = this.state;

    if (!isOpen || blockDefaultHotkeys) {
      return <></>;
    }

    return (
      <Hotkeys>
        <Hotkey
          allowInInput
          preventDefault
          global
          combo="esc"
          label="Close Menu"
          onKeyDown={() => this.closeOptions()}
        />
      </Hotkeys>
    );
  }

  renderSelectPopoverBody(menuWidth) {
    const {
      ariaLabel,
      disabled,
      buttonStyle,
      height,
      label,
      labelId,
      loading,
      small,
      large,
      inlineLabel,
      inputStyle,
      intent,
      minimal,
      onQuery,
      options,
      multi,
      placeholder,
      showCount,
      valueRenderer,
      role,
      // replace the entirely implementation of <Tag> for <Select multi>
      valueTagRenderer,
      valueTagProps,
      className,
      values,
      tagFill,
      width,
      hideSelected // use in conjunction with renderAsButton
    } = this.props;
    let { values: safeValues } = this.props;

    const { isOpen, options: stateOptions, targetComponentName } = this.state;

    // Select from components/select/Select forces values to be [] or '' if empty so for the case of NOT useing Field as parent:
    // IE: multi ? field.getValue() || [] : field.getValue() || '';
    if (values === undefined) {
      safeValues = multi ? [] : '';
    }

    const ariaProps = filterForAriaProps(this.props);
    if (labelId) {
      ariaProps['aria-labelledby'] = labelId;
      ariaProps.labelId = labelId;
    }

    let style = inputStyle;
    if (menuWidth) {
      style = { ...inputStyle, height, width: width || menuWidth };
    }

    if (role) {
      ariaProps.role = role;
    }
    const labelStr = guessTextFromLabel(label);
    const ariaLabelwGuesses = ariaLabel || labelStr || placeholder;

    if (targetComponentName === TARGET_COMPONENT_OPTIONS.MultiSelectInput) {
      return (
        <MultiSelectInput
          {...ariaProps}
          ref={this.targetEl}
          active={isOpen}
          ariaLabel={ariaLabelwGuesses}
          disabled={disabled}
          onClick={this.handleShowOptions}
          onUnselectBuilder={this.handleUnselectItem}
          options={onQuery ? stateOptions : options}
          placeholder={placeholder}
          style={style}
          tagFill={tagFill}
          valueTagRenderer={valueTagRenderer}
          valueRenderer={valueRenderer}
          valueTagProps={valueTagProps}
          className={className}
          values={safeValues}
        />
      );
    }

    if (targetComponentName === TARGET_COMPONENT_OPTIONS.InputGroup) {
      if (!role) {
        ariaProps.role = 'listbox';
      }
      return (
        <InputGroup
          {...ariaProps}
          inputRef={this.targetEl}
          autoComplete="off"
          ariaLabel={ariaLabelwGuesses}
          buttonAriaLabel="clear"
          disabled={disabled}
          type="text"
          onFocus={this.handleTargetFocus}
          onKeyDownCapture={this.handleKeyboardEvents}
          onChange={this.handleAutoCompleteChange}
          style={style}
          small={small}
          large={large}
          value={safeValues}
          width={width || menuWidth}
        />
      );
    }

    if (!role) {
      ariaProps.role = 'listbox';
    }
    return (
      <SelectButton
        {...ariaProps}
        {...buttonStyle}
        ref={this.targetEl}
        ariaLabel={ariaLabelwGuesses}
        active={isOpen}
        disabled={disabled}
        inlineLabel={inlineLabel}
        intent={intent}
        label={label}
        loading={loading}
        minimal={minimal}
        onClick={this.handleShowOptions}
        options={onQuery ? stateOptions : options}
        placeholder={placeholder}
        showCount={showCount}
        small={small}
        valueRenderer={valueRenderer}
        values={safeValues}
        width={width || menuWidth}
        height={height}
        onKeyDownCapture={this.handleEsc}
        hideSelected={hideSelected}
      />
    );
  }

  renderSelectPopover(autoSizerWidth) {
    const {
      exactMatch,
      onQuery,
      multi,
      showFilter,
      values,
      preventEnterSelection,
      keepOpen,
      menuWidth,
      showSelectAll,
      dataTestId
    } = this.props;
    let { values: safeValues } = this.props;

    // menuWidth could be 'auto' (which behaves like min-content) or a number representing pixels in width.
    // If an autosizer width is passed in, and the menuWidth is passed & is a number, take the max of the two.
    // Otherwise use the menuWidth prop (which is passed in, or the default), since it could be a string.

    // Select from components/select/Select forces values to be [] or '' if empty so for the case of NOT useing Field as parent: ..
    // IE: multi ? field.getValue() || [] : field.getValue() || '';
    if (values === undefined) {
      safeValues = multi ? [] : '';
    }

    const { isOpen, selectedIndex, fetchingOptions, targetComponentName } = this.state;
    return (
      <>
        <SelectPopover
          {...this.props}
          menuWidth={autoSizerWidth && Number.isFinite(menuWidth) ? Math.max(menuWidth, autoSizerWidth) : menuWidth}
          canEscapeKeyClose
          isModal={multi}
          isOpen={isOpen}
          onSelectItem={this.handleSelectItem}
          onSelectAll={this.handleSelectItems}
          onClose={this.closeOptions}
          options={this.getFilteredOptions()}
          selectedIndex={selectedIndex}
          values={safeValues}
          popoverRef={this.handlePopoverRef}
          scrollRef={!exactMatch ? this.handleScrollRef : undefined}
          fetchingOptions={fetchingOptions}
          onQuery={showFilter ? onQuery : null}
          preventEnterSelection={preventEnterSelection}
          keepOpen={keepOpen}
          showSelectAll={showSelectAll}
          parentKeyHandler={this.handleKeyboardEvents}
          parentSetIndex={this.setIndexManually}
          focusTopOptionDefault={targetComponentName !== TARGET_COMPONENT_OPTIONS.InputGroup}
          dataTestId={dataTestId}
        >
          {this.renderSelectPopoverBody(autoSizerWidth || menuWidth)}
        </SelectPopover>
      </>
    );
  }

  render() {
    const { style, fill, dataTestId } = this.props;
    const otherProps = {};
    if (dataTestId) {
      otherProps['data-testid'] = dataTestId;
    }

    let popover;
    if (fill) {
      popover = (
        <AutoSizer disableHeight>
          {({ width }) => {
            if (width === 0) {
              // this is really necessary for Safari rendering correctly the select element
              return null;
            }
            return this.renderSelectPopover(width);
          }}
        </AutoSizer>
      );
    } else {
      popover = this.renderSelectPopover();
    }

    return (
      <div ref={this.handleRef} style={style} {...otherProps}>
        {popover}
      </div>
    );
  }
}

export default Select;
